import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { config } from "@/config/env"
import {
  X,
  AlignLeft,
  CheckCircle2,
  ChevronRight,
  FileText,
  MessageSquare,
  Copy,
  Share2,
  Trash2,
  Pencil,
  Play,
  Check,
  Tag,
  User,
  Hash,
  CalendarDays,
  Clock3,
  Users,
  MessageCircle,
  Mail,
  Calendar,
  Eye,
  EyeOff,
  Lock,
  Mic,
  Phone,
  Video as VideoIcon,
  ShieldCheck,
  RefreshCw,
} from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from "@/clientWeb/components/ui/drawer"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import ChatHistory from "./ChatHistory"
import TranscriptionHistory from "./TranscriptionHistory"
import ShareDialog from "./ShareDialog"
import { useMeetingsStore, type Meeting as StoredMeeting } from "../../store/meetings.store"
import { meetingsService, formatMeetingTime, formatMeetingDateTime, isPastMeeting, meetingTimesInZones } from "../../services/meetings.service"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { canonicalTimezone, formatTimezoneLabel, getDefaultTimezone } from "@/shared/utils"
import { getContactsByEmails, type Contact } from "@/features/meetings/data/contacts"
import { getInitials } from "@vc/services/format"

interface MeetingDetailsProps {
  meeting: StoredMeeting | null
  onBack: () => void
}

const PASTEL_COLORS = [
  "#F4D5DB",
  "#D5E3F4",
  "#D5F4D5",
  "#E3D5F4",
  "#F4E3D5",
]

function getAvatarColor(name: string) {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return PASTEL_COLORS[Math.abs(hash) % PASTEL_COLORS.length]
}

import { buildJoinLinks } from '../../utils/joinLinks'
import { displayedMeetingTimes } from '../../utils/actual-duration'
import { isMeetingHost } from '@/features/meetings/services/meeting-host'
import { deleteMeetingCopy, joinLockedUntil, joinPathId } from '@/features/meetings/utils/recurrence'
import { useSeriesScopeActions } from '../../hooks/useSeriesScopeActions'
import { useMarkPast } from '../../hooks/useMarkPast'
import { JoinLockHint } from '../JoinLockHint'
import { useHostMeetingPassword } from "../../hooks/useHostMeetingPassword"
export default function MeetingDetails({ meeting, onBack }: MeetingDetailsProps) {
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const deleteMeeting = useMeetingsStore((s) => s.deleteMeeting)
  const authUser = useAuthStore((s) => s.user)
  const [chatOpen, setChatOpen] = useState(false)
  const [transcriptOpen, setTranscriptOpen] = useState(false)
  const [participantsOpen, setParticipantsOpen] = useState(false)

  // Name the invitees from the meeting itself.
  //
  // `getContactsByEmails` matched addresses against DUMMY_CONTACTS alone and
  // named anyone it missed after their own mailbox, so a real invitee showed up
  // as "aswin@instavc.com" with the same string repeated underneath as their
  // email. `meeting.participants` now carries the whole invited set with real
  // names, so it is the pool. The sample contacts that used to follow it are
  // gone: an invitee whose address matched one was shown under the sample's
  // name, and anyone else is named after their address anyway.
  // Optional-chained because these sit above the `if (!meeting) return null`
  // guard — hooks cannot go after it — so they do run with no meeting selected.
  const contactPool = useMemo<Contact[]>(
    () => [
      ...(meeting?.participants ?? [])
        .filter((p) => p.email)
        .map((p) => ({
          id: p.email as string,
          email: p.email as string,
          name: p.name,
          status: 'active' as const,
        })),
    ],
    [meeting?.participants],
  )
  // The server returns the two kinds of invitee in separate lists — `invitees`
  // for active, `passiveInvitees` for view-only — and only the first was read,
  // so anyone invited on the Passive tab was left off this list. The server
  // keeps the two disjoint, so they are joined without de-duplicating.
  const invitedContacts = useMemo(
    () => getContactsByEmails(
      [...(meeting?.invitees ?? []), ...(meeting?.passiveInvitees ?? [])],
      contactPool,
    ),
    [meeting?.invitees, meeting?.passiveInvitees, contactPool],
  )

  const [shareOpen, setShareOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [meetingUrlCopied, setMeetingUrlCopied] = useState(false)
  const [passiveUrlCopied, setPassiveUrlCopied] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordCopied, setPasswordCopied] = useState(false)
  const seriesActions = useSeriesScopeActions(meeting, {
    onDeleteOneOff: () => setShowDeleteConfirm(true),
    onDeleted: () => onBack(),
  })
  // Back to the list once marked, where it now sits under Past Meetings.
  const markPastAction = useMarkPast(meeting?.id, { onMarked: () => onBack() })

  // Host detection — password and "show password" only render for the host.
  const isHost = useMemo(() => {
    if (!meeting || !authUser) return false
    if (meeting.hostId && authUser.id) return meeting.hostId === authUser.id
    if (meeting.hostEmail && authUser.email) return meeting.hostEmail === authUser.email
    return meeting.host === authUser.name
  }, [meeting, authUser])

  const serverPassword = useHostMeetingPassword(meeting?.id, !!meeting?.settings?.passwordEnabled && isHost)
  const meetingPassword = useMemo(() => {
    if (!meeting || !meeting.settings?.passwordEnabled || !isHost) return undefined
    return serverPassword ?? meetingsService.getPasswordForHost(meeting.id, authUser?.id)
  }, [meeting, isHost, authUser?.id, serverPassword])

  if (!meeting) return null

  const meetingId = meeting.meetingId
  // Booked start, real end if it overran, and the minutes actually spent.
  const shownTimes = displayedMeetingTimes(meeting)
  const duration = `${shownTimes.minutes} minutes`
  // Always the meeting's own schedule, with the viewer's own equivalent added
  // only when it reads a different clock. Shared with the full details page
  // so the two screens cannot disagree about what "the meeting's time" is.
  const zoneTimes = meetingTimesInZones(shownTimes.startMs, shownTimes.endMs, meeting.timezone)
  const meetingZoneId = meeting.timezone ? canonicalTimezone(meeting.timezone) : undefined
  const meetingZoneLabel = zoneTimes.meeting?.zoneLabel ?? formatTimezoneLabel(getDefaultTimezone())
  // Share/copy text names the zone the meeting was scheduled in, not whatever
  // zone happens to be set on the machine doing the copying: this string
  // travels to other people, who are not necessarily reading it from here.
  const eventDate = zoneTimes.meeting && meetingZoneId
    ? new Date(meeting.startTimeMs).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: meetingZoneId,
      })
    : formatMeetingDateTime(meeting.startTimeMs)
  const meetingDateTime = `${eventDate}, ${duration}, ${meetingZoneLabel}`
  // Two links when the meeting separates its audiences, one otherwise. The
  // active link keeps the name `meetingUrl` because it is the meeting's link in
  // every other sense — it is what share, WhatsApp and mail send.
  const links = buildJoinLinks(meeting, config.appBaseUrl)
  const meetingUrl = links.active
  // Room systems dial the meeting's own code — the same number shown as
  // "Meeting ID" above — so nothing extra has to be provisioned or remembered.
  // Both resolve to this meeting through the gateways (see the dial-in resolver).
  const sipAddress = `sip:${meetingId}@${config.sipDomain}`
  const h323Address = `${meetingId}@${config.h323Gateway}`
  // Editable AND yours to edit. The window alone used to decide this, so every
  // invitee saw the pencil on somebody else's meeting — and the screen behind
  // it offers capacity and the invitee list, which are the host's to set.
  // `canModerate` comes from the server; ownership is the fallback for a record
  // read back out of local storage, which carries no permission of its own.
  //
  // Instant meetings too. One stays open after its host leaves until its chosen
  // length runs out, and the host may still want to turn on Cloud Recording or
  // change another setting (BUG-000472). The edit screen and `PUT /meetings/:id`
  // take either kind, and neither changes the type. A guest's `joined` preview
  // is left out because there is no meeting of theirs on the server to edit.
  const isEditableMeeting =
    (meeting.type === "scheduled" || meeting.type === "instant")
    && (meeting.status === "upcoming" || meeting.status === "ongoing")
    && (meeting.canModerate ?? isHost)

  // Inside its scheduled window. `status` here is already derived from the
  // start and end times by the store, so this is the same question the server
  // answers with `resolveStatus`.
  const isInProgress = meeting.status === "ongoing"

  // Delete is a cancellation, and a meeting that has happened cannot be
  // cancelled. See isPastMeeting — one predicate, so this screen and the
  // details page cannot disagree about what "past" means.
  const isPast = isPastMeeting(meeting)
  const deleteCopy = deleteMeetingCopy(meeting, t, language)
  const { markPast: handleMarkPast, markingPast } = markPastAction

  const copyDialAddress = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value)
    toast.success({ title: "Copied", description: `${label} dial-in address copied.` })
  }

  const handleCopyPassiveUrl = async () => {
    if (!links.split) return
    await navigator.clipboard.writeText(links.passive)
    setPassiveUrlCopied(true)
    toast.success({ title: "Copied", description: "View-only link copied to clipboard." })
    setTimeout(() => setPassiveUrlCopied(false), 1600)
  }

  const handleCopyMeetingUrl = async () => {
    await navigator.clipboard.writeText(meetingUrl)
    setMeetingUrlCopied(true)
    toast.success({ title: "Copied", description: "Meeting link copied to clipboard." })
    setTimeout(() => setMeetingUrlCopied(false), 1600)
  }

  const handleEditMeeting = seriesActions.edit

  const handleDeleteMeeting = async () => {
    setShowDeleteConfirm(false)
    try {
      // Server first. This used to be a local-only `removeMeeting`, so the
      // meeting disappeared from the list and came straight back on the next
      // fetch — deleted on screen and untouched everywhere else.
      await deleteMeeting(meeting.id)
      toast.success({ title: deleteCopy.doneTitle, description: deleteCopy.doneDescription })
      onBack()
    } catch {
      toast.error({ title: t("meetings.couldNotDeleteMeeting") })
    }
  }

  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`Join meeting: ${meetingUrl}`)}`, "_blank", "noopener,noreferrer")
  }

  const openEmail = () => {
    window.open(
      `mailto:?subject=${encodeURIComponent(`Meeting: ${meeting.title}`)}&body=${encodeURIComponent(`Join meeting: ${meetingUrl}`)}`,
      "_blank"
    )
  }

  const openGoogleCalendar = () => {
    window.open("https://calendar.google.com/", "_blank", "noopener,noreferrer")
  }

  const openOutlookCalendar = () => {
    window.open("https://outlook.live.com/calendar/", "_blank", "noopener,noreferrer")
  }

  const handleCopyPassword = async () => {
    if (!meetingPassword) return
    await navigator.clipboard.writeText(meetingPassword)
    setPasswordCopied(true)
    toast.success({
      title: t('meetings.passwordCopied'),
      description: t('meetings.passwordCopiedDesc'),
    })
    setTimeout(() => setPasswordCopied(false), 1600)
  }

  const settings = meeting.settings
  const advancedRows = settings
    ? [
        settings.passwordEnabled && { icon: Lock, label: t('meetings.passwordProtected') },
        settings.waitingRoomEnabled && { icon: ShieldCheck, label: t('meetings.waitingRoomEnabled') },
        settings.muteOnEntry && { icon: Mic, label: t('meetings.micMutedOnEntry') },
        settings.videoOnEntry && { icon: VideoIcon, label: t('meetings.videoOnForParticipants') },
        settings.autoRecord && { icon: VideoIcon, label: t('meetings.autoRecording') },
        settings.transcriptionEnabled && { icon: FileText, label: t('meetings.aiTranscription') },
        settings.allowJoinAnytime && { icon: ShieldCheck, label: t('meetings.participantsCanJoinAnytime') },
        meeting.recurring && { icon: RefreshCw, label: t('meetings.recurringMeeting') },
      ].filter(Boolean)
    : []

  return (
    <div className="h-full min-h-0 min-w-0 animate-in fade-in slide-in-from-right-4 duration-300 ease-out flex flex-col">
      <div className="flex-1 overflow-y-auto overflow-x-hidden pr-0.5">
        <div className="bg-card rounded-t-xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-stroke-100 dark:border-border dark:border-slate-800 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
            {meeting.title}
          </h2>
          <div className="flex items-center gap-2">
            {/* Only the host, and only while the meeting is running — there is
                nothing to close off before it starts, and the server refuses it
                outside that window anyway. */}
            {isInProgress && isHost && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={markingPast}
                onClick={handleMarkPast}
                title={t("meetings.markAsPast")}
              >
                <CheckCircle2 className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {markingPast ? t("meetings.markingAsPast") : t("meetings.markAsPast")}
                </span>
              </Button>
            )}
            {isEditableMeeting && (
              <Button
                variant="ghost"
                onClick={handleEditMeeting}
                title={t('common.edit') + ' ' + t('meetings.title')}
                size={'icon'}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            )}
            {/* Same gate as edit: only the host (or a moderator the server
                recognises) may cancel the meeting — never an invitee. */}
            {!isPast && (meeting.canModerate ?? isHost) && (
              <Button
                variant="ghost"
                className="text-error-500"
                onClick={seriesActions.remove}
                title={t('common.delete') + ' ' + t('meetings.title')}
                size={'icon'}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <Button
              onClick={onBack}
              className="w-8 h-8 rounded-lg p-0 flex items-center justify-center bg-primary text-white hover:bg-primary/90 transition-all duration-200"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-4 sm:px-3 flex flex-col gap-2 sm:py-3">
          <div className="p-4 sm:p-4 bg-stroke-100/50 rounded-xl">
            <h3 className="text-md font-semibold text-slate-800 dark:text-white mb-4">{t('meetings.meetingDetails')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <Tag className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{t('meetings.meetingName')}</span>
                </div>
                <p className="text-base text-sm font-semibold text-slate-800 dark:text-slate-100">{meeting.title}</p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <User className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{t('meetings.createdBy')}</span>
                </div>
                <p className="text-base text-sm font-semibold text-slate-800 dark:text-slate-100">{meeting.host}</p>
              </div>
              {/* The description was saved and returned by the server but never
                  shown. Full width, keeping the line breaks it was typed with. */}
              {meeting.description?.trim() && (
                <div className="sm:col-span-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                    <AlignLeft className="w-4 h-4 text-primary dark:text-foreground" />
                    <span className="text-sm font-medium">{t('common.description')}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 whitespace-pre-line break-words">{meeting.description.trim()}</p>
                </div>
              )}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <Hash className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{t('meetings.meetingId')}</span>
                </div>
                <p className="text-base text-sm font-semibold text-slate-800 dark:text-slate-100">{meetingId}</p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <CalendarDays className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{t('meetings.dateAndTime')}</span>
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 break-words">{meetingDateTime}</p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <Clock3 className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{t('meetings.duration')}</span>
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {zoneTimes.meeting
                    ? `${t('meetings.meetingTime')}: ${zoneTimes.meeting.range} ${zoneTimes.meeting.zoneLabel}`
                    : `${formatMeetingTime(shownTimes.startMs)} - ${formatMeetingTime(shownTimes.endMs)}`}
                  {' '}({duration})
                </p>
                {zoneTimes.viewer && (
                  <p className="text-xs text-muted-foreground">
                    {t('meetings.yourTime')}: {zoneTimes.viewer.range} {zoneTimes.viewer.zoneLabel}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <Calendar className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{t('meetings.status')}</span>
                </div>
                <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-muted text-foreground capitalize">
                  {meeting.status}
                </span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <Users className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{t('meetings.seatsLabel')}</span>
                </div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{meeting.seats ?? 2}</p>
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <Share2 className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{links.split ? t('meetings.activeUrlLabel') : t('meetings.meetingUrlLabel')}</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <a href={meetingUrl} target="_blank" rel="noreferrer"
                    className="text-sm text-sky-600 dark:text-sky-400 hover:underline truncate flex-1"
                  >{meetingUrl}</a>
                  <button onClick={handleCopyMeetingUrl}
                    className="shrink-0 text-slate-500 dark:text-slate-400 hover:text-primary transition-colors"
                    title={meetingUrlCopied ? t('meetings.copied') : t('common.copy')}
                  >
                    {meetingUrlCopied
                      ? <Check className="w-4 h-4 text-emerald-500" />
                      : <Copy className="w-4 h-4" />}
                  </button>
                  <button onClick={() => setShareOpen(true)}
                    className="shrink-0 text-slate-500 dark:text-slate-400 hover:text-primary transition-colors"
                    title={t('meetings.shareLink')}
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
                {links.split && (
                  <p className="text-xs text-muted-foreground">{t('meetings.activeLinkHint')}</p>
                )}
              </div>
              {links.split && (
                <div className="sm:col-span-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                    <Eye className="w-4 h-4 text-primary dark:text-foreground" />
                    <span className="text-sm font-medium">{t('meetings.passiveUrlLabel')}</span>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <a href={links.passive} target="_blank" rel="noreferrer"
                      className="text-sm text-sky-600 dark:text-sky-400 hover:underline truncate flex-1"
                    >{links.passive}</a>
                    <button onClick={handleCopyPassiveUrl}
                      className="shrink-0 text-slate-500 dark:text-slate-400 hover:text-primary transition-colors"
                      title={passiveUrlCopied ? t('meetings.copied') : t('common.copy')}
                    >
                      {passiveUrlCopied
                        ? <Check className="w-4 h-4 text-emerald-500" />
                        : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('meetings.passiveLinkHint')}</p>
                </div>
              )}

              {/* H.323 / SIP dial-in for room systems. The number is the meeting
                  code above; the gateways resolve it to this meeting. */}
              <div className="sm:col-span-2 space-y-1.5">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <Phone className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{t('meetings.dialInLabel')}</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 w-12 shrink-0">H.323</span>
                  <span className="text-sm font-mono text-slate-700 dark:text-slate-200 truncate flex-1">{h323Address}</span>
                  <button onClick={() => copyDialAddress(h323Address, 'H.323')}
                    className="shrink-0 text-slate-500 dark:text-slate-400 hover:text-primary transition-colors"
                    title={t('common.copy')}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 w-12 shrink-0">SIP</span>
                  <span className="text-sm font-mono text-slate-700 dark:text-slate-200 truncate flex-1">{sipAddress}</span>
                  <button onClick={() => copyDialAddress(sipAddress, 'SIP')}
                    className="shrink-0 text-slate-500 dark:text-slate-400 hover:text-primary transition-colors"
                    title={t('common.copy')}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">{t('meetings.dialInHint')}</p>
              </div>
            </div>
          </div>

          {/* Security & advanced settings — host sees the password, everyone sees the active toggles */}
          {advancedRows.length > 0 && (
            <div className="p-4 sm:p-5 bg-stroke-100/50 rounded-xl space-y-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <h3 className="text-md font-semibold text-slate-800 dark:text-white">
                  {t('meetings.securityAdvanced')}
                </h3>
              </div>

              {settings?.passwordEnabled && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                    <Lock className="w-4 h-4 text-primary dark:text-foreground" />
                    <span className="text-sm font-medium">{t('meetings.meetingPassword')}</span>
                  </div>
                  {isHost && meetingPassword ? (
                    <div className="flex items-center gap-2 bg-card dark:bg-background/40 border border-stroke-100 dark:border-border rounded-lg px-3 py-2">
                      <code className="flex-1 text-sm font-mono tracking-widest text-slate-800 dark:text-slate-100 truncate">
                        {showPassword ? meetingPassword : '•'.repeat(meetingPassword.length)}
                      </code>
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="text-slate-500 dark:text-slate-400 hover:text-primary transition-colors p-1"
                        title={showPassword ? t('meetings.hidePassword') : t('meetings.showPassword')}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={handleCopyPassword}
                        className="text-slate-500 dark:text-slate-400 hover:text-primary transition-colors p-1 inline-flex items-center gap-1"
                        title={passwordCopied ? t('common.copied') : t('meetings.copyPassword')}
                      >
                        {passwordCopied ? (
                          <Check className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t('meetings.participantsPrompted')}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {advancedRows.map((row, i) => {
                  if (!row) return null
                  const Icon = row.icon
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 bg-card dark:bg-background/40 border border-stroke-100 dark:border-border rounded-lg px-3 py-2"
                    >
                      <Icon className="w-4 h-4 text-primary dark:text-foreground" />
                      <span>{row.label}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="p-4 sm:p-5 bg-stroke-100/50 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-md font-semibold text-slate-800 dark:text-white">{t('meetings.people')}</h3>
              {/* <Button variant="ghost" size="sm" className="text-primary dark:text-foreground gap-1" onClick={() => setParticipantsOpen(true)}>
                <UserPlus className="w-4 h-4" /> {t('common.add')}
              </Button> */}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">{t('meetings.organizer')}</p>
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
                    style={{ backgroundColor: getAvatarColor(meeting.host) }}
                  >
                    {meeting.host.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-slate-800 dark:text-slate-100 truncate">{meeting.host}</p>
                    <p className="text-sm text-muted-foreground">{t('meetings.meetingOrganizer')}</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-primary dark:text-foreground" />
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{t('common.participants')} ({invitedContacts.length})</p>
                </div>
                {invitedContacts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('meetings.noInvitees')}</p>
                ) : (
                  <div className="flex items-center cursor-pointer group" onClick={() => setParticipantsOpen(true)} title="View participants">
                    <div className="flex">
                      {invitedContacts.slice(0, 3).map((contact, index) => {
                        const color = getAvatarColor(contact.name)
                        return (
                          <div
                            key={contact.id}
                            className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-card dark:border-border"
                            style={{ background: color, color: "rgba(0,0,0,0.5)", marginLeft: index > 0 ? "-8px" : "0" }}
                          >
                            {getInitials(contact.name)}
                          </div>
                        )
                      })}
                      {invitedContacts.length > 3 && (
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 border-2 border-card dark:border-slate-600 flex items-center justify-center text-[10px] font-bold text-slate-500 dark:text-slate-400" style={{ marginLeft: "-8px" }}>
                          +{invitedContacts.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-5 bg-stroke-100/50 rounded-xl">
            <h3 className="text-md font-semibold text-slate-800 dark:text-white mb-4">{t('meetings.quickActions')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button variant="outline" className="justify-start gap-2" onClick={openWhatsApp}>
                <MessageCircle className="w-4 h-4 text-emerald-500" /> WhatsApp
              </Button>
              <Button variant="outline" className="justify-start gap-2" onClick={openEmail}>
                <Mail className="w-4 h-4 text-slate-500 dark:text-slate-400" /> Email
              </Button>
              <Button variant="outline" className="justify-start gap-2" onClick={openGoogleCalendar}>
                <Calendar className="w-4 h-4 text-blue-500" /> Google
              </Button>
              <Button variant="outline" className="justify-start gap-2" onClick={openOutlookCalendar}>
                <Calendar className="w-4 h-4 text-indigo-500" /> Outlook
              </Button>
            </div>
          </div>

          {/* {isEditableMeeting && (
            <div className="p-4 sm:p-5 bg-stroke-100/50 rounded-xl space-y-3">
              <h3 className="text-md font-semibold text-slate-800 dark:text-white">{t('meetings.meetingLink')}</h3>
              <div className="flex items-center gap-2 min-w-0">
                <a
                  href={meetingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-sky-600 dark:text-sky-400 hover:underline truncate"
                >
                  {meetingUrl}
                </a>
                <button
                  onClick={handleCopyMeetingUrl}
                  className="inline-flex items-center gap-1.5 text-slate-500 hover:text-primary transition-colors"
                  title={meetingUrlCopied ? t('meetings.copied') : t('common.copy') + ' URL'}
                >
                  {meetingUrlCopied ? (
                    <>
                      <Check className="w-4 h-4 text-emerald-500 animate-in zoom-in-75 duration-200" />
                      <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{t('meetings.copied')}</span>
                    </>
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => setShareOpen(true)}
                  className="text-slate-500 hover:text-primary transition-colors"
                  title={t('meetings.shareLink')}
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )} */}

          {(meeting.status === "completed" || Date.now() > meeting.endTimeMs) && (
            <>
              {/* Transcription */}
              <button onClick={() => setTranscriptOpen(true)} className="w-full flex items-center justify-between py-3 px-4 rounded-xl bg-slate-50 dark:bg-card/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200 group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {t('meetings.transcription')}
                  </span>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 dark:text-foreground dark:group-hover:text-slate-300 transition-colors duration-200" />
              </button>

              {/* Chat History */}
              <button 
                onClick={() => setChatOpen(true)}
                className="w-full flex items-center justify-between py-3 px-4 rounded-xl bg-slate-50 dark:bg-card/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200 group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {t('meetings.chatHistory')}
                  </span>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 dark:text-foreground dark:group-hover:text-slate-300 transition-colors duration-200" />
              </button>
            </>
          )}
        </div>
        </div>

        {/* Chat History Drawer */}
        <ChatHistory open={chatOpen} onOpenChange={setChatOpen} meetingId={meeting.id} />

        {/* Transcription Drawer */}
        <TranscriptionHistory open={transcriptOpen} onOpenChange={setTranscriptOpen} meetingId={meeting.id} />

        {/* Participants Drawer */}
        <Drawer open={participantsOpen} onOpenChange={setParticipantsOpen}>
        <DrawerContent direction="right" width="w-full sm:w-[400px]">
          <DrawerHeader className="px-5 py-3.5 border-b border-stroke-100 dark:border-border dark:border-slate-800 flex flex-row items-center justify-between shrink-0">
            <DrawerTitle className="text-base font-semibold text-slate-800 dark:text-white">
              {t('common.participants')} ({invitedContacts.length})
            </DrawerTitle>
            <DrawerClose asChild>
              <button className="w-8 h-8 rounded-full flex items-center justify-center text-primary border border-primary hover:bg-primary/10 transition-all duration-200">
                <X className="w-4 h-4" />
              </button>
            </DrawerClose>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {invitedContacts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">{t('meetings.noInvitees')}</p>
            ) : (
              <div className="space-y-2">
                {invitedContacts.map((contact) => {
                  const joined = meeting.attendees.some(
                    (a) => a.toLowerCase().includes(contact.name.split(" ")[0].toLowerCase())
                  )
                  // `invitees` is the full participant set, host included — the
                  // backend does not hold it as "everyone except the
                  // organiser" — so without this the person who created the
                  // meeting was listed as "Invited" beside the people they had
                  // invited.
                  // NOT `isHost` — that name is taken in this component for
                  // "the CURRENT USER is the host", and shadowing it inside the
                  // row would make the two impossible to tell apart on sight.
                  const contactIsHost = isMeetingHost(contact, meeting)
                  return (
                    <div
                      key={contact.id}
                      className="flex items-center gap-3 rounded-xl border border-stroke-100 dark:border-border dark:border-border/50 px-3 py-2.5 bg-slate-50/60 dark:bg-card/60"
                    >
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-slate-600 shrink-0"
                        style={{ backgroundColor: getAvatarColor(contact.name) }}
                      >
                        {getInitials(contact.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{contact.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{contact.email}</p>
                      </div>
                      {/* Host outranks both. Being the organiser is not a
                          stage of the invitation it sits alongside — they were
                          never invited, and saying they joined answers a
                          different question than the one the list is asking. */}
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        contactIsHost
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                          : joined
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                            : "bg-primary/10 text-primary"
                      }`}>
                        {contactIsHost ? t('meetings.host') : joined ? t('meetings.joined') : t('meetings.invited')}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </DrawerContent>
        </Drawer>

        <ShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          meetingUrl={meetingUrl}
          sipAddress={sipAddress}
        />

        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm} size="xs">
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {deleteCopy.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deleteCopy.warning}
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={() => void handleDeleteMeeting()}>
              {deleteCopy.confirm}
            </Button>
          </div>
        </DialogContent>
        </Dialog>
        {/* This occurrence / All occurrences, for a day of a series */}
        {seriesActions.dialog}
        {/* Mark as Past while people are still in the room */}
        {markPastAction.dialog}
      </div>

      {/* Sticky Footer */}
      {isEditableMeeting && (
        <div className="shrink-0 border-t rounded-b-xl border-stroke-100 dark:border-border bg-card p-4 sm:p-5">
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onBack}>
              {t('common.cancel')}
            </Button>
            <JoinLockHint lockedUntilMs={joinLockedUntil(meeting)}>
              <Button
                className="gap-2"
                disabled={joinLockedUntil(meeting) !== null}
                onClick={() => navigate(`/meeting/${joinPathId(meeting)}`)}
              >
                <Play className="w-4 h-4" />
                {meeting.status === "upcoming" ? t('meetings.start') : t('meetings.join')}
              </Button>
            </JoinLockHint>
          </div>
        </div>
      )}
    </div>
  )
}
